import React, { useEffect, useState } from "react";
import { CommentsAPI } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2 } from "lucide-react";

/**
 * Byline block with author avatar + Vexent Wire-format timestamp:
 *   "MONDAY, JULY 06, 2026 · 10:55 AM"
 * Renders inline on the article page.
 */
export const Byline = ({ article, avatarSrc }) => {
  const dt = article?.publishedAt ? new Date(article.publishedAt) : null;
  const day = dt
    ? dt
        .toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        .toUpperCase()
    : "";
  const time = dt
    ? dt.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }).toUpperCase()
    : "";
  const authorName = (article?.author || "Vexent Wire Desk").toUpperCase();
  return (
    <div className="flex items-center gap-3" data-testid="article-byline">
      <div className="w-11 h-11 rounded-full overflow-hidden bg-[#0B1F33] shrink-0">
        {avatarSrc && (
          <img
            src={avatarSrc}
            alt={article?.author || "Author"}
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 15%" }}
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="font-condensed uppercase tracking-[0.14em] text-[11px] font-bold text-[#0B1F33]">
          By {authorName}
        </div>
        <div className="mt-0.5 font-mono-tick text-[10px] tracking-[0.14em] text-neutral-500">
          {day}
          {day && time && <span className="mx-1">·</span>}
          {time}
        </div>
      </div>
    </div>
  );
};

/**
 * 6-icon share bar (X, Facebook, LinkedIn, Telegram, Email, Print).
 * All targets open in a new tab. Print triggers window.print(). No trackers.
 */
export const ArticleShareBar = ({ article }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = article?.title || "";
  const text = article?.dek || article?.title || "";
  const enc = encodeURIComponent;
  const targets = [
    {
      id: "x",
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
      svg: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    {
      id: "facebook",
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      svg: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 011.141.195v3.325a8.623 8.623 0 00-.653-.036 26.805 26.805 0 00-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 00-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z"/>
        </svg>
      ),
    },
    {
      id: "linkedin",
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      svg: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.85-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.354V9h3.414v1.561h.048c.476-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.369 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
    {
      id: "telegram",
      label: "Share on Telegram",
      href: `https://t.me/share/url?url=${enc(url)}&text=${enc(title)}`,
      svg: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      ),
    },
    {
      id: "email",
      label: "Share by email",
      href: `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${url}`)}`,
      svg: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
    },
    {
      id: "print",
      label: "Print article",
      onClick: () => { try { window.print(); } catch {} },
      svg: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6,9 6,2 18,2 18,9"/>
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
          <rect x="6" y="14" width="12" height="8"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-2" data-testid="article-share-bar">
      {targets.map((t) =>
        t.href ? (
          <a
            key={t.id}
            href={t.href}
            target="_blank"
            rel="noreferrer"
            aria-label={t.label}
            title={t.label}
            data-testid={`share-${t.id}`}
            className="w-8 h-8 border border-neutral-300 hover:border-[#0B1F33] hover:bg-[#0B1F33] hover:text-[#FFFFFF] text-[#0B1F33] transition-colors inline-flex items-center justify-center"
          >
            {t.svg}
          </a>
        ) : (
          <button
            key={t.id}
            type="button"
            onClick={t.onClick}
            aria-label={t.label}
            title={t.label}
            data-testid={`share-${t.id}`}
            className="w-8 h-8 border border-neutral-300 hover:border-[#0B1F33] hover:bg-[#0B1F33] hover:text-[#FFFFFF] text-[#0B1F33] transition-colors inline-flex items-center justify-center"
          >
            {t.svg}
          </button>
        )
      )}
    </div>
  );
};

/**
 * Minimal own-built comment thread. No third-party embed, no tracker.
 * Comments live in MongoDB under `comments` collection.
 */
export const CommentsThread = ({ articleId }) => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await CommentsAPI.list(articleId);
      setItems(data.comments || []);
      setCount(data.count || 0);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      await CommentsAPI.create(articleId, name.trim(), body.trim());
      setBody("");
      await load();
      toast.success("Comment posted");
    } catch (err) {
      toast.error("Could not post comment", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-12" data-testid="comments-thread">
      <div className="border-t border-neutral-300 pt-6">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-condensed font-bold">
          <MessageCircle className="w-3.5 h-3.5" />
          Discussion · {count} {count === 1 ? "comment" : "comments"}
        </div>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={60}
            required
            className="w-full max-w-[320px] border border-neutral-300 bg-white px-3 py-2 text-[14px] rounded-none focus:border-[#B8E9F1] focus:outline-none"
            data-testid="comment-name-input"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a comment…"
            maxLength={2000}
            required
            rows={3}
            className="w-full border border-neutral-300 bg-white px-3 py-2 text-[14px] rounded-none focus:border-[#B8E9F1] focus:outline-none"
            data-testid="comment-body-input"
          />
          <button
            type="submit"
            disabled={submitting || !name.trim() || !body.trim()}
            className="inline-flex items-center gap-2 bg-[#0B1F33] hover:bg-[#132f57] disabled:opacity-50 text-[#FFFFFF] font-condensed uppercase tracking-[0.18em] text-[11px] font-bold px-4 py-2 transition-colors"
            data-testid="comment-submit-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Post Comment
          </button>
        </form>

        <ul className="mt-8 divide-y divide-neutral-300">
          {loading && <li className="py-4 text-[13px] text-neutral-500">Loading discussion…</li>}
          {!loading && items.length === 0 && (
            <li className="py-4 text-[13px] text-neutral-500">Be the first to comment.</li>
          )}
          {items.map((c) => (
            <li key={c.id} className="py-4" data-testid={`comment-${c.id}`}>
              <div className="flex items-baseline gap-3">
                <span className="font-condensed uppercase tracking-[0.14em] text-[12px] font-bold text-[#0B1F33]">
                  {c.name}
                </span>
                <span className="font-mono-tick text-[10px] tracking-[0.14em] text-neutral-500">
                  {new Date(c.createdAt).toLocaleString("en-US", {
                    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                  }).toUpperCase()}
                </span>
              </div>
              <p className="mt-1.5 text-[14px] leading-[1.5] text-neutral-800 whitespace-pre-wrap">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
